import React, { useMemo, useRef, useState } from 'react';
import { CalendarCheck, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogContentUltraPriority, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { ETAPA_NOME, rotuloFila, useConvidaveis, useCriarTarefa, useOpcoesTarefa, useTiposTarefa, type CategoriaTarefa } from '@/hooks/useTarefas';

interface Props {
  conversaId?: string | null;
  clienteId?: string | null;
  leadId?: string | null;
  pedidoId?: string | null;
  cnpj?: string | null;
  /** Nome do cliente, só pra confirmação do toast. */
  clienteNome?: string | null;
  trigger?: React.ReactElement;
  /** ⛔ `liberaFila` só é verdade na TRIAGEM (Douglas 23/09/2026): lá existe
   *  um próximo caso para liberar. No Painel CRM não há fila nenhuma — o
   *  botão prometia 'liberar próximo' e o backend não libera nada ali. */
  retornoEsim?: { salvar: (dados: { motivo: string; quando: string }) => Promise<void>; liberaFila?: boolean };
  /** Aberto de fora, sem botão (página Tarefas, depois da busca do cliente). */
  open?: boolean;
  onOpenChange?: (aberto: boolean) => void;
  /** Fora do Inbox não há conversa aberta: o servidor liga à do cliente. */
  ligarConversa?: boolean;
  /** Aberto de dentro do Pedido 360 (Sheet z-[10700]): precisa da camada mais alta. */
  sobrePainel?: boolean;
}

/** ISO local (sem UTC) pro <input type="datetime-local">. */
function paraInputLocal(d: Date) {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function atalho(horas: number, horaFixa?: number) {
  const d = new Date();
  if (horaFixa !== undefined) {
    d.setDate(d.getDate() + horas);
    d.setHours(horaFixa, 0, 0, 0);
  } else {
    d.setHours(d.getHours() + horas, 0, 0, 0);
  }
  return d;
}

/**
 * AGENDAR TAREFA (Douglas 04/09/2026) — "o consultor promete algo pro cliente e
 * se esquece de fazer".
 *
 * ⛔ MODAL NO PADRÃO DA CASA (Douglas 23/09/2026): era um balão flutuante com
 * os tipos cortados um por cima do outro. Agora é o mesmo molde da Conferência
 * de eSIM — cabeçalho bordô, tipos inteiros, convidados pela etapa do cliente.
 *
 * Não manda nada pro cliente: grava na timeline do CNPJ, deixa nota interna na
 * conversa e agenda o aviso pra hora marcada.
 */
export function AgendarTarefaPopover({ conversaId, clienteId, leadId, pedidoId, cnpj, clienteNome, trigger, retornoEsim, open, onOpenChange, ligarConversa, sobrePainel }: Props) {
  const Conteudo = sobrePainel ? DialogContentUltraPriority : DialogContent;
  const { profile } = useAuth();
  const { toast } = useToast();
  const criar = useCriarTarefa();

  const [abertoInterno, setAbertoInterno] = useState(false);
  const controlado = open !== undefined;
  const aberto = controlado ? !!open : abertoInterno;
  const setAberto = (v: boolean) => { if (controlado) onOpenChange?.(v); else setAbertoInterno(v); };
  const [categoria, setCategoria] = useState<CategoriaTarefa>('');
  const { data: tipos = [] } = useTiposTarefa();
  /* ⛔ SÓ O QUE CABE NA ETAPA DO CLIENTE, E UMA ABERTA POR VEZ (Douglas
     28/09/2026): "não pode abrir nenhuma tarefa que não tenha a ver com a fila
     que o cliente está... não pode abrir duas tarefas sem fechar a primeira". */
  const opcoes = useOpcoesTarefa(
    { cliente_id: clienteId, lead_id: leadId, cnpj, conversa_id: conversaId, pedido_id: pedidoId, ligar_conversa: ligarConversa && !conversaId ? '1' : null },
    aberto && !retornoEsim,
  );
  const permitidos = opcoes.data?.tipos ?? [];
  const tarefaAberta = retornoEsim ? null : opcoes.data?.aberta ?? null;
  /* A TAREFA VAI PARA O CONSULTOR DONO (Douglas 30/09/2026): quem abre e não é
     o dono entra como ajuda. Sem dono, não se abre tarefa. */
  const dono = retornoEsim ? null : opcoes.data?.dono ?? null;
  const semDono = !retornoEsim && opcoes.isSuccess && !dono;
  const conversaLigada = ligarConversa && !conversaId && opcoes.isSuccess ? opcoes.data?.conversa ?? null : undefined;
  const leadLigado = ligarConversa && !leadId && opcoes.isSuccess ? opcoes.data?.lead ?? null : undefined;
  const souDono = !!dono && dono.id === profile?.id;
  const tiposVisiveis = retornoEsim
    ? tipos.filter((t) => t.slug === 'retornar_atendimento')
    : tipos.filter((t) => permitidos.includes(t.slug));
  const categoriaValida = retornoEsim ? 'retornar_atendimento'
    : tiposVisiveis.some((t) => t.slug === categoria) ? categoria : (tiposVisiveis[0]?.slug ?? '');
  const tipoEscolhido = tipos.find((t) => t.slug === categoriaValida);
  const podeConvidar = tipoEscolhido?.convida !== false;
  const [quando, setQuando] = useState(() => paraInputLocal(atalho(2)));
  const [titulo, setTitulo] = useState('');
  const [convidados, setConvidados] = useState<string[]>([]);
  const [salvando, setSalvando] = useState(false);
  const emEnvio = useRef(false);
  const [erro, setErro] = useState('');
  /* ⛔ QUEM PODE AJUDAR É DO BANCO (Douglas 23/09/2026): a régua lê a fila do
     Kanban do cliente AGORA (fn_tarefa_convidaveis). A tela só mostra. */
  const convidaveis = useConvidaveis(
    { cliente_id: clienteId, lead_id: leadId, cnpj, conversa_id: conversaId, categoria: categoriaValida },
    aberto && !retornoEsim && !!categoriaValida && podeConvidar,
  );
  const listaConvidaveis = convidaveis.data?.convidaveis ?? [];
  const filaCliente = convidaveis.data?.fila ?? null;
  /* QUEM AJUDA POR REGRA (Douglas 30/09/2026): nos casos da carteira o tipo já
     traz quem ajuda — vem marcado e não se desmarca. O resto é escolha. */
  const obrigatorios = listaConvidaveis.filter((c) => c.obrigatorio).map((c) => c.usuario_id);
  const todosConvidados = [...new Set([...obrigatorios, ...convidados])];

  const atalhos = useMemo(() => ([
    { rotulo: 'Em 2h', valor: paraInputLocal(atalho(2)) },
    { rotulo: 'Amanhã 9h', valor: paraInputLocal(atalho(1, 9)) },
    { rotulo: '+3 dias', valor: paraInputLocal(atalho(3, 9)) },
  ]), [aberto]);

  const trocarTipo = (slug: string) => { setCategoria(slug); setConvidados([]); };

  const alternar = (id: string) =>
    setConvidados((atual) => atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]);

  const salvar = async () => {
    if (emEnvio.current) return;
    setErro('');
    if (!retornoEsim && tarefaAberta) { setErro('Este cliente já tem uma tarefa aberta. Finalize-a antes de abrir outra.'); return; }
    if (!retornoEsim && !categoriaValida) { setErro('Nenhum tipo de tarefa cabe na etapa deste cliente agora.'); return; }
    const t = titulo.trim();
    if (!t) { setErro('Diga o que ficou de fazer.'); return; }
    const d = new Date(quando);
    if (Number.isNaN(d.getTime())) { setErro('Data e hora inválidas.'); return; }
    if (retornoEsim && (t.length < 5 || t.length > 1000 || d.getTime() <= Date.now())) {
      setErro('Informe um horário futuro e uma justificativa de 5 a 1.000 caracteres.');
      return;
    }
    emEnvio.current = true;
    setSalvando(true);
    try {
      if (retornoEsim) {
        await retornoEsim.salvar({ motivo: t, quando: d.toISOString() });
      }
      const criada = retornoEsim ? null : await criar.mutateAsync({
        titulo: t,
        categoria: categoriaValida,
        vence_em: d.toISOString(),
        conversa_id: conversaId || undefined,
        cliente_id: clienteId || undefined,
        lead_id: leadId || undefined,
        pedido_id: pedidoId || undefined,
        cnpj: cnpj || undefined,
        convidados: todosConvidados.length && podeConvidar ? todosConvidados : undefined,
        ligar_conversa: ligarConversa && !conversaId ? true : undefined,
      });
      toast({
        title: 'Tarefa criada',
        description: `Em Minhas tarefas · ${d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}${todosConvidados.length && podeConvidar ? ` · ${todosConvidados.length} convidado(s)` : ''}${criada && ligarConversa && !conversaId && !criada.conversa_id ? ' · sem conversa: a nota não foi ao Inbox' : ''}`,
      });
      setTitulo(''); setConvidados([]);
      setAberto(false);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      emEnvio.current = false;
      setSalvando(false);
    }
  };

  const rotuloSecao = 'mb-1.5 text-[10px] font-bold uppercase tracking-[1.2px] text-muted-foreground';

  return (
    <Dialog open={aberto} onOpenChange={(v) => { if (!salvando) setAberto(v); }}>
      {!controlado && <DialogTrigger asChild>
        {trigger || <Button type="button" variant="outline" size="sm" className="w-full h-7 text-xs gap-1.5 justify-start">
          <CalendarCheck className="h-3.5 w-3.5 text-muted-foreground" /> Nova tarefa
        </Button>}
      </DialogTrigger>}
      <Conteudo className="w-[calc(100%-2rem)] max-w-[500px] max-h-[88vh] flex flex-col overflow-hidden p-0 gap-0 rounded-2xl sm:rounded-2xl bg-background [&>button]:hidden">
        <div className="flex shrink-0 items-center gap-3 bg-[linear-gradient(135deg,#4A1320_0%,#5B0A0D_55%,#6E1626_100%)] px-4 py-3.5">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-white/[0.12] text-white"><CalendarCheck className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1">
            <DialogTitle className="font-display text-[15px] font-bold tracking-[-0.3px] text-white">
              {retornoEsim ? 'Agendar retorno' : 'Nova tarefa'}
            </DialogTitle>
            {clienteNome && <p className="truncate text-[10.5px] text-white/65" title={clienteNome}>{clienteNome}</p>}
          </div>
          <button type="button" aria-label="Fechar" disabled={salvando} onClick={() => setAberto(false)}
            className="grid h-[26px] w-[26px] place-items-center rounded-lg border border-white/[0.14] bg-white/[0.12] text-white hover:bg-white/[0.22]">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {retornoEsim && <p className="text-[11px] text-muted-foreground">Cria uma tarefa em Minhas tarefas{retornoEsim.liberaFila ? ' e libera o próximo caso da fila' : ''} até o retorno. A linha continua pendente, sem alterar pagamento.</p>}

          {!retornoEsim && tarefaAberta && (
            <div role="alert" className="rounded-lg border border-primary/40 bg-primary/5 px-2.5 py-2">
              <p className="text-[9.5px] font-bold uppercase tracking-[1.1px] text-primary">Já existe uma tarefa aberta</p>
              <p className="text-[11.5px] font-semibold text-foreground">{tarefaAberta.titulo}</p>
              <p className="text-[10.5px] text-muted-foreground">
                {tarefaAberta.responsavel_nome ? `De ${tarefaAberta.responsavel_nome.split(' ')[0]}. ` : ''}
                Um cliente tem uma tarefa por vez: finalize esta antes de abrir outra, ou peça ajuda nela.
              </p>
            </div>
          )}

          {!retornoEsim && !tarefaAberta && <div>
            <p className={rotuloSecao}>Tipo</p>
            {opcoes.isSuccess && (
              <p className="mb-1.5 text-[10.5px] text-muted-foreground">
                Etapa do cliente: <span className="font-semibold text-primary">{(opcoes.data?.etapas ?? []).map((e) => ETAPA_NOME[e] || e).join(' · ') || '—'}</span>
              </p>
            )}
            {opcoes.isLoading && <p className="text-[11px] text-muted-foreground">carregando…</p>}
            {opcoes.error && <p role="alert" className="text-[11px] text-destructive">{(opcoes.error as Error).message}</p>}
            <div className="grid grid-cols-2 gap-1.5">
              {tiposVisiveis.map((c) => (
                <button key={c.slug} type="button" onClick={() => trocarTipo(c.slug)} title={c.descricao ?? undefined}
                  className={cn(
                    'flex h-[30px] items-center rounded-lg border px-2.5 text-left text-[11.5px] transition-colors',
                    categoriaValida === c.slug
                      ? 'border-primary bg-primary/10 font-semibold text-primary'
                      : 'border-border bg-card text-foreground hover:border-primary/40',
                  )}>
                  <span className="truncate">{c.nome}</span>
                </button>
              ))}
            </div>
          </div>}

          <div>
            <p className={rotuloSecao}>Quando</p>
            <div className="flex flex-wrap items-center gap-1.5">
              <input type="datetime-local" value={quando} onChange={(e) => setQuando(e.target.value)}
                className="h-[29px] rounded-lg border border-border bg-background px-2 text-[11.5px] focus:outline-none focus:ring-1 focus:ring-primary" />
              {atalhos.map((a) => (
                <button key={a.rotulo} type="button" onClick={() => setQuando(a.valor)}
                  className={cn('h-[29px] rounded-lg border px-2.5 text-[11px] transition-colors',
                    quando === a.valor ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card text-muted-foreground hover:border-primary/40')}>
                  {a.rotulo}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className={rotuloSecao}>{retornoEsim ? 'Justificativa / o que ficou de fazer' : 'O que ficou de fazer'} <span className="text-primary">*</span></p>
            <textarea value={titulo} onChange={(e) => setTitulo(e.target.value)} rows={3}
              maxLength={retornoEsim ? 1000 : 160}
              placeholder={retornoEsim ? 'Cliente vai trocar o chip e enviar a foto' : 'Confirmar o número da linha nova com o cliente'}
              className="w-full resize-none rounded-lg border border-border bg-background px-2.5 py-2 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>

          {!retornoEsim && !tarefaAberta && podeConvidar && <div>
            <p className={rotuloSecao}>Convidar para ajudar</p>
            {convidaveis.isSuccess && <div className="mb-2 rounded-lg border border-border-warm bg-[#FAF8F4] px-2.5 py-1.5 dark:bg-[#241A1A]">
              <p className="text-[9.5px] font-bold uppercase tracking-[1.1px] text-muted-foreground">Etapa do cliente agora</p>
              <p className="truncate text-[11.5px] font-semibold text-primary">{rotuloFila(filaCliente)}</p>
              {filaCliente?.mensagem && <p className="truncate text-[10.5px] text-muted-foreground" title={filaCliente.mensagem}>{filaCliente.mensagem}</p>}
            </div>}
            {convidaveis.isLoading && <p className="text-[11px] text-muted-foreground">carregando…</p>}
            {convidaveis.error && <p role="alert" className="text-[11px] text-destructive">{(convidaveis.error as Error).message}</p>}
            {convidaveis.isSuccess && !listaConvidaveis.length && <p className="text-[11px] text-muted-foreground">Ninguém pode ser convidado para este cliente agora.</p>}
            <div className="flex flex-wrap gap-1.5">
              {listaConvidaveis.map((c) => {
                const fixo = !!c.obrigatorio;
                const on = fixo || convidados.includes(c.usuario_id);
                return (
                  <button key={c.usuario_id} type="button" onClick={() => { if (!fixo) alternar(c.usuario_id); }}
                    disabled={fixo} title={fixo ? 'Ajuda por regra deste caso: entra sempre.' : undefined}
                    className={cn('inline-flex h-[27px] items-center gap-1 rounded-full border px-2.5 text-[11px] transition-colors',
                      on ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-border bg-card text-foreground hover:border-primary/40',
                      fixo && 'cursor-default')}>
                    {on && <Check className="h-3 w-3" />}
                    {c.nome.split(' ')[0]} <span className="text-muted-foreground font-normal">· {c.papel}</span>
                  </button>
                );
              })}
            </div>
            {obrigatorios.length > 0 && <p className="mt-1 text-[10.5px] text-muted-foreground">Os marcados com fundo ajudam por regra deste caso e entram sempre. Os outros você chama se quiser que acompanhem.</p>}
            {todosConvidados.length > 0 && <p className="mt-1 text-[10.5px] text-muted-foreground">Cada convidado recebe um aviso e responde por escrito. {dono && !souDono ? `A tarefa é de ${dono.nome.split(' ')[0]}: quem finaliza é o dono.` : 'A tarefa continua sua: quem finaliza é você.'}</p>}
          </div>}

          {conversaLigada !== undefined && (
            <p className="text-[10.5px] text-muted-foreground">
              Conversa: {conversaLigada
                ? <span className="font-semibold text-primary">{conversaLigada.conexao || 'linha do cliente'}{conversaLigada.telefone ? ` · ${conversaLigada.telefone}` : ''}</span>
                : <span className="font-semibold text-primary">o cliente não tem conversa no Inbox. A tarefa abre e grava na timeline, sem a nota na conversa.</span>}
            </p>
          )}
          {leadLigado !== undefined && (
            <p className="text-[10.5px] text-muted-foreground">
              Lead: {leadLigado
                ? <span className="font-semibold text-primary">{[leadLigado.tipo, leadLigado.status].filter(Boolean).join(' · ').replace(/_/g, ' ').toLowerCase()}</span>
                : <span className="font-semibold text-primary">nenhum lead aberto deste cliente.</span>}
            </p>
          )}
          {semDono && <p role="alert" className="text-[11px] text-destructive">Este cliente não tem consultor dono. A tarefa é sempre do dono: resolva o dono no Busca 360 antes de abrir.</p>}
          <p className="text-[10.5px] leading-snug text-muted-foreground">
            {retornoEsim || souDono || !dono
              ? <>Fica no seu nome ({profile?.nome?.split(' ')[0] || 'você'}), em Minhas tarefas. </>
              : <>Vai para <b className="font-semibold text-primary">{dono.nome.split(' ')[0]}</b>, consultor dono do cliente, que finaliza. Você entra como ajuda e recebe Tarefa concluída. </>}
            Não manda nada ao cliente: grava na timeline, deixa a nota na conversa e avisa na hora marcada.
          </p>
          {erro && <p role="alert" className="text-[11px] text-destructive">{erro}</p>}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-border-warm px-4 py-2.5">
          <Button type="button" variant="outline" className="h-[29px] px-3 text-[11.5px]" disabled={salvando} onClick={() => setAberto(false)}>Cancelar</Button>
          <Button type="button" onClick={salvar} disabled={salvando || (!retornoEsim && (!!tarefaAberta || !categoriaValida || semDono))} className="h-[29px] gap-1.5 px-3 text-[11.5px] text-white">
            <CalendarCheck className="h-3.5 w-3.5" />
            {salvando ? 'Salvando…' : retornoEsim ? (retornoEsim.liberaFila ? 'Agendar e liberar próximo' : 'Agendar retorno') : 'Criar tarefa'}
          </Button>
        </div>
      </Conteudo>
    </Dialog>
  );
}
